import { App } from 'antd';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';

import { getViewUrl } from '@/apis/avatar';
import { useApiHandler } from '@/hooks';

interface Props {
  currentPath?: string;
  onFileChange: (file: File | null) => void;
}

const AvatarUploader = ({ currentPath, onFileChange }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const { handleApiCall } = useApiHandler();
  const { message } = App.useApp();

  useEffect(() => {
    if (currentPath) {
      const fn = async () => {
        const viewUrl = await handleApiCall(() => getViewUrl(), {
          context: '取得頭像 URL',
        });
        if (viewUrl) {
          setAvatarUrl(viewUrl.url);
        }
        setPreview(null);
        setFile(null);
      };

      fn();
    }
  }, [currentPath, handleApiCall]);

  const selectFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/jpeg', 'image/png'].includes(f.type)) {
      message.warning('僅允許 JPG/PNG 格式');
      return;
    }

    // 檢查圖片比例
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;

        // 驗證比例必須為 4:3 (允許 1% 的誤差)
        const aspectRatio = width / height;
        const targetRatio = 4 / 3;
        const tolerance = 0.01;

        if (Math.abs(aspectRatio - targetRatio) > tolerance) {
          message.warning('圖片比例必須為 4:3');
          return;
        }

        // 比例正確，設置檔案和預覽
        setFile(f);
        setPreview(URL.createObjectURL(f));
      };
      img.onerror = () => {
        message.warning('無法讀取圖片');
      };
      img.src = event.target?.result as string;
    };
    reader.onerror = () => {
      message.warning('無法讀取檔案');
    };
    reader.readAsDataURL(f);
  };

  useEffect(() => {
    onFileChange(file);
  }, [file, onFileChange]);

  return (
    <div>
      {(preview || avatarUrl) && (
        <img src={preview || avatarUrl || ''} alt="avatar" />
      )}
      <input type="file" accept="image/jpeg,image/png" onChange={selectFile} />
    </div>
  );
};

export default AvatarUploader;
